// Envuelve a <grid-slider> y le añade autoplay + dots + control pausa/play.
// No duplica la lógica de arrastre: reutiliza la mecánica scrollTo del motor base.
class WimCardSlider extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.dots = [];
    this.activeIndex = 0;
    this.isPaused = false;
    this.onMouseEnter = () => this.pause();
    this.onMouseLeave = () => this.resume();
    this.onFocusIn = () => this.pause();
    this.onFocusOut = () => this.resume();
  }

  connectedCallback() {
    this.track = this.querySelector('[data-grid-slider]');
    this.slides = Array.from(this.querySelectorAll('[data-grid-item]'));
    if (!this.track || !this.slides.length) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled = this.hasAttribute('data-autoplay') && !this.reducedMotion;
    this.autoplayMs = parseInt(this.getAttribute('data-autoplay-ms'), 10) || 4000;

    if (this.hasAttribute('data-dots')) this.buildControls();

    this.syncActiveOnScroll();

    if (this.autoplayEnabled) {
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    if (this.scrollHandler) this.track.removeEventListener('scroll', this.scrollHandler);
  }

  buildControls() {
    const controls = document.createElement('div');
    controls.className = 'wim-card-slider__controls';

    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'wim-card-slider__dots';
    dotsWrap.setAttribute('role', 'tablist');

    const dotTpl = this.getAttribute('data-dot-label') || 'Go to slide {{ number }}';

    this.slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wim-card-slider__dot';
      dot.setAttribute('aria-label', dotTpl.replace('{{ number }}', index + 1));
      if (index === 0) dot.setAttribute('aria-current', 'true');
      dot.addEventListener('click', () => this.goTo(index));
      dotsWrap.appendChild(dot);
      this.dots.push(dot);
    });

    controls.appendChild(dotsWrap);

    if (this.autoplayEnabled) {
      this.toggleBtn = document.createElement('button');
      this.toggleBtn.type = 'button';
      this.toggleBtn.className = 'wim-card-slider__toggle';
      this.pauseLabel = this.getAttribute('data-pause-label') || 'Pause';
      this.playLabel = this.getAttribute('data-play-label') || 'Play';
      this.toggleBtn.setAttribute('aria-label', this.pauseLabel);
      this.toggleBtn.textContent = '❚❚';
      this.toggleBtn.addEventListener('click', () => this.toggle());
      controls.appendChild(this.toggleBtn);
    }

    this.appendChild(controls);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ top: 0, left: slide.offsetLeft, behavior: 'smooth' });
    this.setActive(index);
  }

  next() {
    const index = (this.activeIndex + 1) % this.slides.length;
    this.goTo(index);
  }

  setActive(index) {
    this.activeIndex = index;
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  // Mantiene el dot activo sincronizado con drag/flechas del motor base.
  syncActiveOnScroll() {
    let ticking = false;
    this.scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const left = this.track.scrollLeft;
        let nearest = 0;
        let min = Infinity;
        this.slides.forEach((slide, i) => {
          const diff = Math.abs(slide.offsetLeft - left);
          if (diff < min) {
            min = diff;
            nearest = i;
          }
        });
        if (nearest !== this.activeIndex) this.setActive(nearest);
        ticking = false;
      });
    };
    this.track.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => this.next(), this.autoplayMs);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  pause() {
    if (!this.autoplayEnabled || this.isPaused) return;
    this.stop();
  }

  resume() {
    if (!this.autoplayEnabled || this.isPaused) return;
    this.start();
  }

  toggle() {
    this.isPaused = !this.isPaused;
    if (this.isPaused) {
      this.stop();
      this.toggleBtn.setAttribute('aria-label', this.playLabel);
      this.toggleBtn.textContent = '▶';
    } else {
      this.start();
      this.toggleBtn.setAttribute('aria-label', this.pauseLabel);
      this.toggleBtn.textContent = '❚❚';
    }
  }
}

if (!customElements.get('wim-card-slider')) {
  customElements.define('wim-card-slider', WimCardSlider);
}
